import { IconType } from 'react-icons'
import {
    FaBullhorn,
    FaChartPie,
    FaClipboardList,
    FaClock,
    FaHospital,
    FaPaw,
} from 'react-icons/fa6'

import { UserRoutes } from '~config/routes'
import { SidebarItem, useSidebarContent } from '../tabs'
import { ClinicFeatureFieldsFragment } from '~graphql/generated/graphql'

export type NewSidebarSectionId =
    | 'daily-operations'
    | 'clinical-lookups'
    | 'client-outreach'
    | 'insights'
    | 'patient-portal'
    | 'clinic-configuration'

/**
 * Pointer to a page in the legacy sidebar tree. Lookup is by route (stable
 * across renames); `label` falls back to label matching for pages whose route
 * is environment-dependent (external links) and doubles as the display label,
 * which is how renames like "Clients & Patients" -> "Patient & Owner Records"
 * are applied.
 */
type PageRef = {
    to?: string
    label: string
}

export type NewSidebarSubgroup = {
    label: string
    /** Setup page the subgroup divider links to. */
    to: string
    pages: SidebarItem[]
}

export type NewSidebarEntry =
    | { kind: 'page'; page: SidebarItem }
    | { kind: 'subgroup'; subgroup: NewSidebarSubgroup }

export type NewSidebarSection = {
    id: NewSidebarSectionId
    label: string
    icon: IconType
    /**
     * Render as a static section label with always-visible pages instead of
     * a collapsible dropdown.
     */
    labelOnly?: boolean
    entries: NewSidebarEntry[]
}

type SectionBlueprintEntry =
    | { kind: 'page'; ref: PageRef }
    | { kind: 'subgroup'; label: string; to: string; refs: PageRef[] }

type SectionBlueprint = {
    id: NewSidebarSectionId
    label: string
    icon: IconType
    labelOnly?: boolean
    entries: SectionBlueprintEntry[]
}

const SECTIONS: SectionBlueprint[] = [
    {
        id: 'daily-operations',
        label: 'Daily Operations',
        icon: FaClock,
        labelOnly: true,
        entries: [
            { kind: 'page', ref: { to: UserRoutes.Chats, label: 'Inbox' } },
            {
                kind: 'page',
                ref: { to: UserRoutes.Whiteboard, label: 'Whiteboard' },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.PendingActions,
                    label: 'Pending Actions',
                },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.DailyHuddle, label: 'Daily Huddle' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.TeamChat, label: 'Team Chat' },
            },
        ],
    },
    {
        id: 'clinical-lookups',
        label: 'Clinical Lookups',
        icon: FaClipboardList,
        entries: [
            {
                kind: 'page',
                ref: { to: UserRoutes.Appointments, label: 'Appointments' },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.ClientsPatients,
                    label: 'Patient & Owner Records',
                },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Payments, label: 'Payments' },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.NotificationLogs,
                    label: 'Message Logs',
                },
            },
        ],
    },
    {
        id: 'client-outreach',
        label: 'Client Outreach',
        icon: FaBullhorn,
        entries: [
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.ComplianceTracker,
                    label: 'Compliance Tracker',
                },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.MassCommunication,
                    label: 'Announcements',
                },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Forms, label: 'Digital Forms' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Loyalty, label: 'Loyalty' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Reviews, label: 'Reviews' },
            },
        ],
    },
    {
        id: 'insights',
        label: 'Insights',
        icon: FaChartPie,
        entries: [
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicMetrics, label: 'Metrics' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicKpiDashboard, label: 'KPIs' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicReporting, label: 'Reporting' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicTrends, label: 'Trends' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Campaigns, label: 'Campaigns' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.Goals, label: 'Goals' },
            },
        ],
    },
    {
        id: 'patient-portal',
        label: 'Patient Portal',
        icon: FaPaw,
        entries: [
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.PortalCustomization,
                    label: 'Customization',
                },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.UserTracking,
                    label: 'User Tracking',
                },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.MarketingMaterials,
                    label: 'Marketing Materials',
                },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.PetSelfies, label: 'Pet Selfies' },
            },
            {
                kind: 'page',
                ref: {
                    to: UserRoutes.PrescriptionRefillRequests,
                    label: 'Prescription Refills',
                },
            },
            // External link with an environment-dependent URL, so it is
            // matched by label instead of route.
            { kind: 'page', ref: { label: 'Open Patient Portal' } },
        ],
    },
    {
        id: 'clinic-configuration',
        label: 'Clinic Configuration',
        icon: FaHospital,
        entries: [
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicInfo, label: 'Clinic Info' },
            },
            {
                kind: 'page',
                ref: { to: UserRoutes.ClinicUsers, label: 'Users' },
            },
            {
                kind: 'subgroup',
                label: 'Booking Setup',
                to: UserRoutes.BookingSettings,
                refs: [
                    {
                        to: UserRoutes.Clinicians,
                        label: 'Clinicians',
                    },
                    { to: UserRoutes.Rooms, label: 'Rooms' },
                    { to: UserRoutes.Services, label: 'Services' },
                    {
                        to: UserRoutes.SchedulingRules,
                        label: 'Scheduling Rules',
                    },
                    {
                        to: UserRoutes.AvailabilityChecker,
                        label: 'Availability Checker',
                    },
                    { to: UserRoutes.Widgets, label: 'Widgets' },
                ],
            },
            {
                kind: 'subgroup',
                label: 'Communication Setup',
                to: UserRoutes.EngagementSettings,
                refs: [
                    { to: UserRoutes.Audiences, label: 'Audiences' },
                    {
                        to: UserRoutes.SharedMedia,
                        label: 'Shared Media',
                    },
                    {
                        to: UserRoutes.MessageTemplates,
                        label: 'Message Templates',
                    },
                    {
                        to: UserRoutes.EmailTemplates,
                        label: 'Email Templates',
                    },
                    {
                        to: UserRoutes.Notifications,
                        label: 'Notifications',
                    },
                ],
            },
        ],
    },
]

function collectLegacyPages(items: SidebarItem[]): SidebarItem[] {
    return items.flatMap(item =>
        item.pages ? [item, ...collectLegacyPages(item.pages)] : [item],
    )
}

export function useNewSidebarContent(
    features: ClinicFeatureFieldsFragment | null | undefined,
): {
    sections: NewSidebarSection[]
    leftoverItems: SidebarItem[]
} {
    const { sidebarItems } = useSidebarContent(features)

    const allPages = collectLegacyPages(sidebarItems)
    const byRoute = new Map(
        allPages.filter(page => page.to).map(page => [page.to as string, page]),
    )
    const resolveRef = (ref: PageRef): SidebarItem | undefined => {
        const found = ref.to
            ? byRoute.get(ref.to)
            : allPages.find(page => page.label === ref.label)
        if (!found) return undefined
        return { ...found, label: ref.label }
    }

    const sections: NewSidebarSection[] = SECTIONS.map(section => ({
        ...section,
        entries: section.entries.flatMap((entry): NewSidebarEntry[] => {
            if (entry.kind === 'page') {
                const page = resolveRef(entry.ref)
                return page ? [{ kind: 'page', page }] : []
            }
            const pages = entry.refs.flatMap(ref => {
                const page = resolveRef(ref)
                return page ? [page] : []
            })
            return pages.length > 0
                ? [
                      {
                          kind: 'subgroup',
                          subgroup: {
                              label: entry.label,
                              to: entry.to,
                              pages,
                          },
                      },
                  ]
                : []
        }),
    })).filter(section => section.entries.length > 0)

    // Pages outside the new structure stay exactly as they are in the legacy
    // sidebar (labels, grouping, order) and are always visible.
    const leftoverItems = [
        byRoute.get(UserRoutes.Calls),
        byRoute.get(UserRoutes.DataSync),
        sidebarItems.find(item => item.label === 'Settings'),
        sidebarItems.find(item => item.label === 'Help Center'),
    ].filter((item): item is SidebarItem => !!item)

    return {
        sections,
        leftoverItems,
    }
}

export type ResolvedPinnedPage = {
    page: SidebarItem
    /** Home section, for unpin-confirmation copy. */
    sectionLabel: string
}

/**
 * Maps pinned routes (in pin order) to the sidebar rows they came from so
 * the Pinned submenu renders the same labels and icons. Covers top-level
 * pages, subgroup setup pages, and subgroup pages; stale pins for routes
 * that no longer resolve are skipped.
 */
export function resolvePinnedItems(
    sections: NewSidebarSection[],
    pinnedRoutes: string[],
): ResolvedPinnedPage[] {
    const byRoute = new Map<string, ResolvedPinnedPage>()
    for (const section of sections) {
        for (const entry of section.entries) {
            if (entry.kind === 'page') {
                if (entry.page.to && !byRoute.has(entry.page.to)) {
                    byRoute.set(entry.page.to, {
                        page: entry.page,
                        sectionLabel: section.label,
                    })
                }
            } else {
                const { subgroup } = entry
                if (subgroup.to && !byRoute.has(subgroup.to)) {
                    byRoute.set(subgroup.to, {
                        page: {
                            to: subgroup.to,
                            label: subgroup.label,
                            icon: section.icon,
                        },
                        sectionLabel: section.label,
                    })
                }
                for (const page of subgroup.pages) {
                    if (page.to && !byRoute.has(page.to)) {
                        byRoute.set(page.to, {
                            page,
                            sectionLabel: section.label,
                        })
                    }
                }
            }
        }
    }
    return pinnedRoutes.flatMap(route => {
        const resolved = byRoute.get(route)
        return resolved ? [resolved] : []
    })
}
